import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
export class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  #userPaused = false;

  #abortController = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #nearViewportObserver;

  /** @type {IntersectionObserver | undefined} */
  #farViewportObserver;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    // Media play/pause events don't bubble — listen in the capture phase to keep isPlaying
    // and the toggle hint in sync with actual playback (autoplay videos start playing
    // without going through playMedia).
    this.addEventListener('play', this.#handlePlaybackChange, { capture: true, signal });
    this.addEventListener('pause', this.#handlePlaybackChange, { capture: true, signal });

    // Viewport-gated autoplay: the eager <video> isn't rendered (see video.liquid), so an
    // offscreen clip never downloads or decodes until the shopper scrolls near it.
    if (this.hasAttribute('data-autoplay-when-visible')) {
      this.#observeAutoplayViewport();
      return;
    }

    const video = this.querySelector('video');
    if (video && !video.paused) {
      // An autoplaying video may have started before this element upgraded and its
      // play event was missed — sync the initial state.
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
    } else if (video && this.hasAttribute('autoplay')) {
      // Dynamically inserted markup (e.g. fetched product recommendations) doesn't
      // adopt the muted attribute as state, so the browser blocks its unmuted
      // autoplay — restore the state and kick playback manually. For videos from
      // the initial server render this is a no-op alongside native autoplay.
      video.muted = video.hasAttribute('muted');
      if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
      video.play().catch(() => {});
    }
  }

  /**
   * Syncs component state with the video's actual playback state.
   * @param {Event} event - The play or pause event.
   */
  #handlePlaybackChange = (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;
    this.isPlaying = event.type === 'play';
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Pauses the media when a dialog closes, except for autoplay media outside the
   * dialog (e.g. the product media gallery behind the zoom dialog), which resumes
   * unless the user deliberately paused it.
   */
  #handleDialogClose = () => {
    if (this.hasAttribute('autoplay') && !this.closest('dialog') && !this.#userPaused) {
      requestAnimationFrame(() => this.playMedia());
    } else {
      this.pauseMedia();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#nearViewportObserver?.disconnect();
    this.#farViewportObserver?.disconnect();
  }

  /**
   * Gates autoplay on viewport proximity. The near observer loads + plays the clip a
   * little before it scrolls into view and pauses it on the way out; the far observer
   * detaches the source once the media is well off-screen so a clip that was reached
   * stops buffering. Reuses loadContent()'s WebKit autoplay hardening (muted restore +
   * load()), the same path the click-to-play and zoom-dialog flows lean on.
   */
  #observeAutoplayViewport() {
    this.#nearViewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#playInViewport();
          else this.pauseMedia();
        }
      },
      // Start a little before the media is visible — vertically (desktop grid) and
      // horizontally (mobile carousel swipe) — so it's already playing on arrival.
      { rootMargin: '300px' }
    );
    this.#nearViewportObserver.observe(this);

    this.#farViewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) this.#unloadDeferredVideo();
        }
      },
      // Only unload once well out of view, so small scrolls near the fold don't churn
      // through reload/poster-flash cycles.
      { rootMargin: '150% 0px' }
    );
    this.#farViewportObserver.observe(this);
  }

  /**
   * Loads (on first approach) and plays the gated video, unless the shopper paused it.
   */
  #playInViewport() {
    if (this.#userPaused) return;

    if (this.getAttribute('data-media-loaded')) {
      this.playMedia();
    } else {
      // Silent: an ambient autoplay video shouldn't pause the shopper's other media.
      this.loadContent(false, { silent: true });
    }
  }

  /**
   * Detaches the gated video's source so a far-offscreen clip stops buffering. The
   * <template> still holds a pristine copy, so the next approach re-loads it.
   */
  #unloadDeferredVideo() {
    if (!this.getAttribute('data-media-loaded')) return;

    const media = this.querySelector('video, iframe');
    if (media instanceof HTMLVideoElement) {
      media.pause();
      media.removeAttribute('src');
      for (const source of media.querySelectorAll('source')) source.removeAttribute('src');
      media.load(); // applies the detach and aborts the in-flight download
    }
    media?.remove();

    this.removeAttribute('data-media-loaded');
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
  }

  /**
   * Re-evaluates the viewport gate after a morph reset the loaded state, so an in-view
   * video resumes following a variant change. Called by MediaGallery.updatedCallback.
   */
  refreshAutoplayGate() {
    if (!this.hasAttribute('data-autoplay-when-visible')) return;
    this.#nearViewportObserver?.disconnect();
    this.#farViewportObserver?.disconnect();
    this.#observeAutoplayViewport();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   * @param {object} [options]
   * @param {boolean} [options.silent] - Skips the MediaStartedPlayingEvent so this load
   *   doesn't pause other media. Used by the viewport autoplay gate (ambient playback).
   */
  loadContent(focus = true, { silent = false } = {}) {
    if (this.getAttribute('data-media-loaded')) return;

    if (!silent) this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    // hasAttribute, not getAttribute: a bare boolean attribute reads as '' (falsy)
    if (content instanceof HTMLVideoElement && content.hasAttribute('autoplay')) {
      // Cloned videos don't reliably adopt the muted attribute as state in WebKit,
      // which blocks gesture-free playback on iOS — restore it before playing.
      content.muted = content.hasAttribute('muted');
      // WebKit stalls play() on a freshly-inserted video that uses <source> children
      // (as Shopify's video_tag renders) — run resource selection explicitly first.
      if (content.readyState === HTMLMediaElement.HAVE_NOTHING) content.load();
      // force autoplay for safari
      content.play().catch(() => {});
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.#userPaused = true;
      this.pauseMedia();
    } else if (
      this.hasAttribute('data-autoplay-when-visible') &&
      !this.hasAttribute('data-media-loaded')
    ) {
      this.#userPaused = false;
      this.loadContent(false);
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    this.#userPaused = false;

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')
        ?.play()
        .catch(() => {});
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
